import React, { useState, useEffect } from 'react';
import DiscountHeader from './DiscountHeader';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { Grid, Container, Card } from '@mui/material';
import Footer from 'src/components/Footer';
import { DiscountsContent } from './DiscountsContent';
import { useAuth } from 'src/hooks/useAuth';
import { useDiscountAPI } from 'src/hooks/useDiscountAPI';
import { DiscountCode } from './discounts.interface';

const PAGE_SIZE = 30;

const DiscountsIndex = () => {
  const { enhancedFetch } = useAuth();
  const { getDiscountCodes, loading: apiLoading } = useDiscountAPI();
  const [discounts, setDiscounts] = useState<DiscountCode[]>([]);
  const [filteredDiscounts, setFilteredDiscounts] = useState<DiscountCode[]>(
    []
  );
  const [sortedDiscounts, setSortedDiscounts] = useState<DiscountCode[]>([]);
  const [currentDiscount, setCurrentDiscount] = useState<DiscountCode | null>(
    null
  );
  const [selectedDiscount, setSelectedDiscount] = useState<DiscountCode | null>(
    null
  );
  const [loadingDiscountDetails, setLoadingDiscountDetails] = useState(false);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  const [selectedSortStatus, setSelectedSortStatus] = useState<string>('');
  const [selectedFilterStatus, setSelectedFilterStatus] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE);
  const [totalDiscounts, setTotalDiscounts] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const fetchDiscounts = async () => {
    setLoading(true);
    try {
      const data = await getDiscountCodes({
        page: page,
        size: rowsPerPage
      });

      if (data) {
        setDiscounts(data.content);
        setTotalDiscounts(data.totalElements);
      }
    } catch (error) {
      console.error('Error fetching discounts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Discount Details when currentDiscount is set
  useEffect(() => {
    if (currentDiscount && typeof currentDiscount === 'string') {
      setLoadingDiscountDetails(true);
      enhancedFetch(`/api/v1/discount-codes/${currentDiscount}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
        .then((data) => {
          setCurrentDiscount(data);
        })
        .catch((error) => {
          console.error('Error fetching discount details:', error);
        })
        .finally(() => {
          setLoadingDiscountDetails(false);
        });
    }
  }, [currentDiscount]);

  // Fetch Discounts by Page
  useEffect(() => {
    fetchDiscounts().then();
  }, [page, rowsPerPage]);

  // Refresh discounts when refresh state changes
  useEffect(() => {
    if (refresh) {
      fetchDiscounts();
    }
  }, [refresh]);

  // Set up status options based on discount applies to values
  useEffect(() => {
    const uniqueStatuses = [
      'ALL_VENDORS_ALL_PRODUCTS',
      'ALL_VENDORS',
      'ALL_PRODUCTS',
      'SELECTED_VENDORS_AND_SELECTED_PRODUCTS'
    ];
    setStatusOptions(uniqueStatuses);
  }, []);

  // Update filtered discounts based on filter state change
  useEffect(() => {
    if (selectedFilterStatus === '' || selectedFilterStatus === 'all') {
      setFilteredDiscounts(discounts);
    } else {
      const filtered = discounts.filter(
        (discount) => discount.appliesTo === selectedFilterStatus
      );
      setFilteredDiscounts(filtered);
    }
  }, [selectedFilterStatus, discounts, refresh]);



  const updateDiscountStatus = async (
    discountId: string,
    newStatus: string
  ): Promise<void> => {
    try {
      await enhancedFetch(`/api/v1/discount-codes/${discountId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ appliesTo: newStatus })
      });

      // Update the discounts state locally
      setDiscounts((prevDiscounts) =>
        prevDiscounts?.map((discount) =>
          discount.id === discountId
            ? { ...discount, appliesTo: newStatus }
            : discount
        )
      );

      // Update filteredDiscounts if necessary
      setFilteredDiscounts((prevFiltered) =>
        prevFiltered?.map((discount) =>
          discount.id === discountId
            ? { ...discount, appliesTo: newStatus }
            : discount
        )
      );
    } catch (error) {
      console.error('Error updating discount status:', error);
    }
  };

  return (
    <>
      <title>Discounts</title>
      <PageTitleWrapper>
        <DiscountHeader
          statusOptions={statusOptions}
          selectedSortStatus={selectedSortStatus}
          setSelectedSortStatus={setSelectedSortStatus}
          selectedFilterStatus={selectedFilterStatus}
          setSelectedFilterStatus={setSelectedFilterStatus}
        />
      </PageTitleWrapper>
      <Container maxWidth="lg">
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="stretch"
          spacing={3}
        >
          <Grid item xs={12}>
            <Card>
              <DiscountsContent
                onRefresh={() => {
                  setRefresh(!refresh);
                }}
                loading={loading || apiLoading}
                discounts={
                  selectedSortStatus ? sortedDiscounts : filteredDiscounts
                }
                setDiscounts={setDiscounts}
                currentDiscount={currentDiscount}
                setCurrentDiscount={setCurrentDiscount}
                updateDiscountStatus={updateDiscountStatus}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalDiscounts={totalDiscounts}
                setTotalDiscounts={setTotalDiscounts}
              />
            </Card>
          </Grid>
        </Grid>
      </Container>
      <Footer />
    </>
  );
};

export default DiscountsIndex;
